import { createFileRoute, useParams, Navigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { handleGetGeometryGeometriesGeometryIdGetOptions } from '../../../api/generated/@tanstack/react-query.gen';
import { UUID_REGEX, asUuid } from '@/lib/uuid';
import { useGeometryLineage } from '@/hooks/useGeometryLineage';
import { useStaleWarning } from '@/hooks/useStaleWarning';
import { revealControl } from '@/utils/revealControl';
import { httpStatus, isBelowEditorRole } from '@/utils/shareAccess';
import {
  GeometryNotFound,
  GeometryErrorBoundary,
} from '../-components/GeometryNotFound';
import { DiagnosisWorkspace } from './-components/DiagnosisWorkspace';

/** What `/diagnosis/$uuid` carries in its search — see `validateDiagnosisSearch`. */
interface DiagnosisSearch {
  geometry?: string;
  study?: string;
  dp?: string;
  conversation?: string;
}

function DiagnosisRoute() {
  const { t } = useTranslation();
  const { uuid } = useParams({ from: '/_app/diagnosis/$uuid' });
  const isValidUuid = UUID_REGEX.test(uuid);

  const {
    data: geometry,
    error,
    isError,
  } = useQuery({
    ...handleGetGeometryGeometriesGeometryIdGetOptions({
      path: { geometry_id: uuid },
    }),
    retry: false,
    enabled: isValidUuid,
  });
  // Staleness is judged by the lineage TIP's last diagnosis run (falling back
  // to the tip's upload time for never-diagnosed versions). The route uuid is
  // always a lineage ROOT (library links point at roots), but the Re-run
  // button diagnoses the tip — keying off the root made the warning
  // un-clearable for multi-version lineages and nagged freshly revised ones.
  // Root fields are only the fallback while the lineage loads; the query is
  // shared with the workspace tree, so this hits the same cache.
  const lineageQuery = useGeometryLineage(isValidUuid ? uuid : null);
  const lineageTip = lineageQuery.data?.versions?.at(-1);
  const staleTimestamp = lineageTip
    ? (lineageTip.diagnosis_at ?? lineageTip.created_at)
    : (geometry?.diagnosis_at ?? geometry?.created_at);
  useStaleWarning(
    staleTimestamp,
    t('compCommon.staleWarning.messageGeometry'),
    {
      label: t('compCommon.staleWarning.actionRerunDiagnosis'),
      onAction: () => revealControl('[data-stale-cta="rerun-diagnosis"]'),
    },
  );

  if (!isValidUuid) {
    return (
      <GeometryNotFound
        message={t('diagnosisRoutes.route.invalidUrl')}
        dashboardTo="/diagnosis"
      />
    );
  }

  if (isError) {
    // By status: a 404 arrives as the parsed error body, not an `Error`.
    if (httpStatus(error) === 404) {
      return (
        <GeometryNotFound
          message={t('diagnosisRoutes.route.notFound')}
          dashboardTo="/diagnosis"
        />
      );
    }
    // Non-404 errors (500, network down) — let the workspace load; it surfaces
    // its own diagnosis error states and recovers when the backend returns.
  }

  // Below-editor roles (viewer/commenter) get the read-only share viewer
  // instead of the editable Diagnosis workspace — the same gate `/meshgen/$uuid`
  // already applies (ENG-1068). Diagnosis was the one product route missing it,
  // and it mounts the re-diagnose / simplify / approve / reject actions, which
  // are all editor-gated server-side. Without this a viewer sees live-looking
  // controls that fail with a confusing "not found".
  if (geometry && isBelowEditorRole(geometry.my_role)) {
    return <Navigate to="/share/geometry/$uuid" params={{ uuid }} replace />;
  }

  // key={uuid} forces a clean remount when switching geometries. The
  // workspace reads and writes this route's search itself.
  return <DiagnosisWorkspace key={uuid} projectId={uuid} />;
}

/**
 * `geometry` — the selected version when it is not the lineage's current one
 * (synced with `activeGeometryId`); `study` + `dp` — the study a CadMorph
 * hand-off came from, attached to every edit request as context;
 * `conversation` — the assistant conversation this workspace's edits live in.
 * Anything that is not a UUID is dropped.
 */
export function validateDiagnosisSearch(
  search: Record<string, unknown>,
): DiagnosisSearch {
  return {
    geometry: asUuid(search.geometry),
    study: asUuid(search.study),
    dp: asUuid(search.dp),
    conversation: asUuid(search.conversation),
  };
}

export const Route = createFileRoute('/_app/diagnosis/$uuid')({
  validateSearch: validateDiagnosisSearch,
  component: DiagnosisRoute,
  errorComponent: (props) => (
    <GeometryErrorBoundary {...props} dashboardTo="/diagnosis" />
  ),
});
